import { LiveUpdate } from "@capawesome/capacitor-live-update";
import { App } from "@capacitor/app";
import { Capacitor } from "@capacitor/core";

/**
 * THE SECOND WAY A WEB BUNDLE CAN REACH THIS APP.
 *
 * Until this module existed there was exactly one: `make ios-dev` / `ios-staging` /
 * `ios-prod` builds `dist/`, `cap sync` copies it into the binary, and an Xcode build phase
 * refuses to compile when the stamp in that bundle disagrees with the identity being built
 * (ios/scripts/check-env-stamp.sh). That guard is the only thing standing between "looks
 * completely normal" and "writes to the wrong database".
 *
 * An over-the-air channel walks straight past it. So this seam is deliberately thin and
 * deliberately dumb: it hands four verbs to `app/lib/ota.ts`, which is where every refusal
 * is decided. Nothing here decides whether an update may be applied.
 *
 * ## Two things the plugin does that no JavaScript here can undo, and both are the point
 *
 * 1. **It verifies the bundle's signature in Swift, before the archive is unpacked.** The
 *    public key is compiled into the NATIVE SHELL from `capacitor.config.ts`, one key per
 *    environment, so a bundle signed for staging cannot be unpacked by a production build
 *    at all. That is the environment stamp, enforced where an updated bundle cannot reach
 *    it. Read `verifyFile` in the plugin's `ios/Plugin/LiveUpdate.swift`: with a public key
 *    configured, a missing signature is `signatureMissing` and a wrong one is
 *    `signatureVerificationFailed` — it fails closed in both directions.
 * 2. **It rolls back to the bundle compiled into the binary when the app never marks itself
 *    healthy.** `readyTimeout` in `capacitor.config.ts` starts a timer at launch; `ready()`
 *    cancels it. A bundle that throws before `markHealthy()` is reverted without the app
 *    getting a say, which is the only reason a bad update cannot brick an install.
 *
 * ## Why every call here swallows its own failure
 *
 * A JavaScript bundle can be NEWER THAN THE SHELL IT RUNS INSIDE — that is the whole point
 * of the channel — and someone will be carrying a months-old binary. So an update pushed
 * today may execute inside a shell built before this plugin was added, where `LiveUpdate` is
 * not registered at all and every call rejects, or inside one whose plugin predates a method
 * this file names. Both must mean "no channel", never a rejected promise reaching start-up.
 *
 * That is the same discipline as the additive-migration rule, pointed at the bridge instead
 * of the database: NEVER call a method that did not exist in the oldest shell in the field,
 * and treat every call as though it might not be there.
 */
const isNativeRuntime = Capacitor.isNativePlatform();

/** What the shell is — not what the bundle running inside it thinks it is. */
export interface ShellIdentity {
  /** The iOS bundle identifier, e.g. `ch.mediahirsch.buddy.staging`. */
  appId: string;
  /** `CFBundleShortVersionString` — the version of the BINARY, not of the web bundle. */
  version: string;
}

export interface DownloadRequest {
  bundleId: string;
  url: string;
  /** Base64 RSA PKCS#1 v1.5 over the SHA-256 digest of the archive. */
  signature: string;
  /**
   * SHA-256 of the archive, hex. Belt and braces: the plugin checks the SIGNATURE when a
   * public key is configured and falls back to the checksum when one is not, so sending
   * both means a shell that somehow shipped without a key still refuses bytes nobody named.
   */
  checksum: string;
}

export interface HealthResult {
  /** True when the previous launch never marked itself healthy and was reverted. */
  rolledBack: boolean;
}

export const updates = {
  /**
   * Whether this shell can apply an update at all. False on the web, false in a binary
   * built before the plugin was added — which is every build currently in anyone's hands.
   */
  isSupported(): boolean {
    return isNativeRuntime && Capacitor.isPluginAvailable("LiveUpdate");
  },

  /**
   * THE ONE FACT AN UPDATED BUNDLE CANNOT LIE ABOUT.
   *
   * `VITE_APP_ENV` is compiled into the WEB BUNDLE, so a staging bundle running inside a
   * production shell reports "staging" for both halves of any comparison it makes with
   * itself — the check is a tautology and passes. The bundle identifier comes from the
   * shell's own `Info.plist` and is untouched by anything shipped over the air, which is
   * what makes it the runtime form of the build-phase stamp check.
   */
  async shellIdentity(): Promise<ShellIdentity | null> {
    if (!isNativeRuntime) return null;
    const info = await App.getInfo().catch(() => null);
    if (!info) return null;
    return { appId: info.id, version: info.version };
  },

  /**
   * The bundle the plugin believes is running, or `null` for the one compiled into the
   * binary. Note it is the plugin's bookkeeping, not the running code's opinion of itself —
   * `app/lib/ota.ts` compares the two, because a disagreement means one of them is wrong.
   */
  async currentBundleId(): Promise<string | null> {
    if (!updates.isSupported()) return null;
    const result = await LiveUpdate.getCurrentBundle().catch(() => null);
    return result?.bundleId ?? null;
  },

  /**
   * "THIS BUNDLE BOOTED." Cancels the native rollback timer.
   *
   * Called on EVERY launch, including the built-in bundle's, because the timer is armed when
   * the plugin loads rather than when an update is applied. On the built-in bundle a missed
   * call costs nothing; on an updated one it is the difference between an install that
   * recovers by itself and one somebody has to delete and reinstall.
   *
   * `null` means the shell cannot answer — no plugin, or a browser.
   */
  async markHealthy(): Promise<HealthResult | null> {
    if (!updates.isSupported()) return null;
    const result = await LiveUpdate.ready().catch(() => null);
    if (!result) return null;
    return { rolledBack: result.rollback === true };
  },

  /**
   * Fetch and unpack, WITHOUT applying. The signature is verified in Swift first; a bundle
   * that fails leaves nothing on disk and returns false.
   */
  async download(request: DownloadRequest): Promise<boolean> {
    if (!updates.isSupported()) return false;
    return LiveUpdate.downloadBundle({
      bundleId: request.bundleId,
      url: request.url,
      signature: request.signature,
      checksum: request.checksum,
    })
      .then(() => true)
      .catch(() => false);
  },

  /**
   * Arm a downloaded bundle for the NEXT cold launch. Deliberately not `reload()`: swapping
   * the bundle under someone mid-task throws away whatever they were in the middle of, and
   * a phone is not a browser tab you can refresh. iOS reclaims a backgrounded app often
   * enough that "next launch" is usually minutes away.
   */
  async stage(bundleId: string): Promise<boolean> {
    if (!updates.isSupported()) return false;
    return LiveUpdate.setNextBundle({ bundleId })
      .then(() => true)
      .catch(() => false);
  },

  /**
   * BACK TO THE BUNDLE INSIDE THE BINARY, NOW — the manual form of what the rollback timer
   * does. `reset()` alone only changes what the NEXT launch uses, which would leave a bundle
   * we have just decided is wrong running for the rest of the session; `reload()` puts the
   * built-in bundle on screen immediately.
   */
  async revertToBuiltIn(): Promise<boolean> {
    if (!updates.isSupported()) return false;
    return LiveUpdate.reset()
      .then(() => LiveUpdate.reload())
      .then(() => true)
      .catch(() => false);
  },
};
